import React from 'react'

// The rim light that runs along the edge of a card, on the side facing the
// pointer.
//
// It depends on the pointer's direction from the card's centre alone, not on
// its distance: the highlight sits where the line from the centre to the
// pointer crosses the card's edge, and it is as bright from across the page
// as from next to the card. A lit element receives that point in its own
// coordinates as two CSS variables, --light-x and --light-y, plus the
// highlight's radius as --light-radius, and its stylesheet draws a radial
// highlight centred there, clipped to a one-pixel ring along the edge.

interface Point {
	x: number
	y: number
}

///@brief   Radius of the highlight as a share of the box's longer side, so
///         it covers the same stretch of the edge on a small card as on a
///         wide scene and the direction reads the same on both.
const LIGHT_RADIUS_RATIO: number = 0.6

///@brief   Where the line from the box's centre towards (x, y), both in
///         viewport coordinates, crosses the box's edge — in the box's own
///         coordinates. A pointer on the centre itself lights the top edge.
export function rimLightPoint( rect: DOMRect, x: number, y: number ): Point {
	const hw: number = rect.width / 2
	const hh: number = rect.height / 2
	const dx: number = x - rect.left - hw
	const dy: number = y - rect.top - hh

	if ( dx === 0 && dy === 0 ) return { x: hw, y: 0 }

	const t: number = Math.min(
		dx === 0 ? Infinity : hw / Math.abs( dx ),
		dy === 0 ? Infinity : hh / Math.abs( dy ),
	)

	return { x: hw + dx * t, y: hh + dy * t }
}

///@brief   Lights `el` from the pointer at (x, y), in viewport coordinates.
export function applyRimLight( el: HTMLElement, x: number, y: number ): void {
	const rect: DOMRect = el.getBoundingClientRect()
	const light: Point = rimLightPoint( rect, x, y )
	el.style.setProperty( '--light-x', `${ light.x }px` )
	el.style.setProperty( '--light-y', `${ light.y }px` )
	el.style.setProperty( '--light-radius', `${ Math.max( rect.width, rect.height ) * LIGHT_RADIUS_RATIO }px` )
}

///@brief   Lights the elements matching `selector` under `rootRef`, or the
///         root itself when no selector is given. Listening on the window
///         rather than on the cards keeps the light coherent while the
///         pointer is between or above them; scrolling repaints, since the
///         cards move under a pointer that has not.
export function useCursorRimLight( rootRef: React.RefObject<HTMLElement | null>, selector?: string ): void {

	React.useEffect( (): ( () => void ) => {
		const root: HTMLElement | null = rootRef.current
		if ( root === null ) return (): void => {}

		const cards: HTMLElement[] = selector === undefined
			? [ root ]
			: Array.from( root.querySelectorAll<HTMLElement>( selector ) )
		let pointer: Point | null = null
		let frame: number = 0

		const paint = (): void => {
			frame = 0
			if ( pointer === null ) return
			for ( const card of cards ) {
				applyRimLight( card, pointer.x, pointer.y )
			}
		}

		const schedule = (): void => {
			if ( frame === 0 ) frame = window.requestAnimationFrame( paint )
		}

		const onMove = ( event: MouseEvent ): void => {
			pointer = { x: event.clientX, y: event.clientY }
			schedule()
		}

		window.addEventListener( 'mousemove', onMove )
		window.addEventListener( 'scroll', schedule, { passive: true } )

		return (): void => {
			window.removeEventListener( 'mousemove', onMove )
			window.removeEventListener( 'scroll', schedule )
			if ( frame !== 0 ) window.cancelAnimationFrame( frame )
		}
	}, [ rootRef, selector ] )
}
